import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, DollarSign, BarChart3, Brain, Plus, Trash2, X, Loader, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BusinessAnalytics = () => {
  const [activeTab, setActiveTab] = useState('productivity');
  const [productivity, setProductivity] = useState(null);
  const [spending, setSpending] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodRes, spendRes, projRes] = await Promise.all([
        axios.get(`${API}/business-analytics/productivity`, { withCredentials: true }),
        axios.get(`${API}/business-analytics/spending-summary`, { withCredentials: true }),
        axios.get(`${API}/business-analytics/projects`, { withCredentials: true }),
      ]);
      setProductivity(prodRes.data);
      setSpending(spendRes.data);
      setProjects(projRes.data.projects || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'productivity', label: 'Productivity', icon: TrendingUp },
    { id: 'spending', label: 'Spending', icon: DollarSign },
    { id: 'resources', label: 'Resources', icon: BarChart3 },
    { id: 'cba', label: 'Cost-Benefit', icon: Brain },
  ];

  if (loading) {
    return (
      <div className="border-2 border-[var(--border-default)] bg-[var(--surface)] p-8">
        <div className="flex items-center gap-3">
          <Loader size={18} className="animate-spin" />
          <span className="text-sm text-[var(--text-secondary)]">Loading business analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="business-analytics" className="border-2 border-[var(--border-default)] bg-[var(--surface)] mb-8">
      {/* Header */}
      <div className="border-b-2 border-[var(--border-default)] p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 size={18} className="text-[var(--primary)]" />
          <h2 className="text-lg font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            BUSINESS ANALYTICS
          </h2>
        </div>
        <p className="text-xs text-[var(--text-muted)]">Productivity, spending, resources & cost-benefit analysis</p>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b-2 border-[var(--border-default)] overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`ba-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[var(--surface-alt)] text-[var(--text-primary)] border-b-2 border-[var(--primary)] -mb-[2px]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4 sm:p-6">
        {activeTab === 'productivity' && <ProductivityTab data={productivity} />}
        {activeTab === 'spending' && <SpendingTab data={spending} projects={projects} onRefresh={fetchData} />}
        {activeTab === 'resources' && <ResourcesTab projects={projects} onRefresh={fetchData} />}
        {activeTab === 'cba' && <CostBenefitTab onRefresh={fetchData} />}
      </div>
    </div>
  );
};

/* ---- Productivity Tab ---- */
const ProductivityTab = ({ data }) => {
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Ideas" value={data.total_ideas} />
        <MetricCard label="This Week" value={data.ideas_this_week} accent="var(--secondary)" />
        <MetricCard label="This Month" value={data.ideas_this_month} accent="var(--primary)" />
        <MetricCard label="AI Enhanced" value={data.enhanced_count} accent="var(--warning)" />
      </div>

      {/* Weekly Trend */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] mb-3">Weekly Trend (12 weeks)</h4>
        <div className="flex items-end gap-1 h-24">
          {data.weekly_trend?.map((week, i) => {
            const max = Math.max(...data.weekly_trend.map(w => w.count), 1);
            const height = (week.count / max) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-[var(--secondary)] opacity-80 transition-all min-h-[2px]"
                  style={{ height: `${Math.max(height, 2)}%` }}
                  title={`${week.week}: ${week.count} ideas`}
                />
                {i % 3 === 0 && (
                  <span className="text-[9px] text-[var(--text-muted)] hidden sm:block">{week.week}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Categories & Tags */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] mb-3">Categories</h4>
          <div className="space-y-2">
            {data.categories?.slice(0, 6).map(cat => (
              <div key={cat.category} className="flex items-center justify-between">
                <span className="text-sm">{cat.category}</span>
                <span className="text-xs font-mono text-[var(--text-muted)]">{cat.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)] mb-3">Top Tags</h4>
          <div className="flex flex-wrap gap-2">
            {data.top_tags?.map(tag => (
              <span key={tag.tag} className="text-xs px-2 py-1 bg-[var(--surface-alt)] border border-[var(--border-subtle)]">
                {tag.tag} ({tag.count})
              </span>
            ))}
            {(!data.top_tags || data.top_tags.length === 0) && (
              <span className="text-xs text-[var(--text-muted)]">No tags used yet</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---- Spending Tab ---- */
const SpendingTab = ({ data, projects, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [budget, setBudget] = useState('');
  const [spent, setSpent] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/business-analytics/projects`, {
        project_name: name,
        total_budget: parseFloat(budget) || 0,
        spent: parseFloat(spent) || 0,
        resources: []
      }, { withCredentials: true });
      toast.success('Project added');
      setName(''); setBudget(''); setSpent('');
      setShowForm(false);
      onRefresh();
    } catch (err) {
      toast.error('Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/business-analytics/projects/${id}`, { withCredentials: true });
      toast.success('Project removed');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Budget" value={`$${(data?.total_budget || 0).toLocaleString()}`} />
        <MetricCard label="Total Spent" value={`$${(data?.total_spent || 0).toLocaleString()}`} accent="var(--primary)" />
        <MetricCard label="Remaining" value={`$${(data?.remaining || 0).toLocaleString()}`} accent="var(--secondary)" />
        <MetricCard label="Utilization" value={`${data?.utilization_percent || 0}%`} accent="var(--warning)" />
      </div>

      {/* Projects List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)]">Projects</h4>
          <button
            data-testid="add-project-btn"
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1 text-xs font-bold text-[var(--secondary)] hover:opacity-80"
          >
            <Plus size={12} /> Add Project
          </button>
        </div>

        {showForm && (
          <div className="border border-[var(--border-default)] p-3 mb-3 bg-[var(--surface-alt)] space-y-2">
            <input
              placeholder="Project name"
              value={name} onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                placeholder="Budget ($)"
                type="number" value={budget} onChange={e => setBudget(e.target.value)}
                className="px-3 py-2 text-sm border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
              />
              <input
                placeholder="Spent ($)"
                type="number" value={spent} onChange={e => setSpent(e.target.value)}
                className="px-3 py-2 text-sm border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
              />
            </div>
            <button
              onClick={handleCreate} disabled={saving}
              className="text-xs font-bold px-4 py-2 bg-[var(--secondary)] text-white border border-[var(--border-default)] disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Project'}
            </button>
          </div>
        )}

        {projects.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">No projects yet. Add one to track spending.</p>
        ) : (
          <div className="space-y-2">
            {projects.map((p, i) => {
              const pct = p.total_budget > 0 ? (p.spent / p.total_budget * 100) : 0;
              return (
                <div key={i} className="border border-[var(--border-subtle)] p-3 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{p.project_name}</div>
                    <div className="text-xs text-[var(--text-muted)]">
                      ${p.spent?.toLocaleString()} / ${p.total_budget?.toLocaleString()}
                    </div>
                    <div className="mt-1 h-1.5 bg-[var(--surface-alt)] border border-[var(--border-subtle)]">
                      <div className="h-full bg-[var(--secondary)]" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                  <span className="text-xs font-mono text-[var(--text-muted)]">{pct.toFixed(0)}%</span>
                  <button onClick={() => handleDelete(p.project_id)} className="text-[var(--text-muted)] hover:text-[var(--primary)]">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---- Resources Tab ---- */
const ResourcesTab = ({ projects, onRefresh }) => {
  const [expandedProject, setExpandedProject] = useState(null);
  const [resName, setResName] = useState('');
  const [resType, setResType] = useState('tools');
  const [resCost, setResCost] = useState('');
  const [resNotes, setResNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const addResource = async (project) => {
    if (!resName.trim()) return;
    setSaving(true);
    const updatedResources = [...(project.resources || []), {
      name: resName, type: resType, estimated_cost: parseFloat(resCost) || 0, notes: resNotes
    }];
    try {
      await axios.put(`${API}/business-analytics/projects/${project.project_id}`, {
        project_name: project.project_name,
        total_budget: project.total_budget,
        spent: project.spent,
        resources: updatedResources,
        notes: project.notes || ''
      }, { withCredentials: true });
      toast.success('Resource added');
      setResName(''); setResCost(''); setResNotes('');
      onRefresh();
    } catch (err) {
      toast.error('Failed to add resource');
    } finally {
      setSaving(false);
    }
  };

  const resourceTypes = ['people', 'tools', 'budget', 'other'];

  return (
    <div className="space-y-4">
      <p className="text-xs text-[var(--text-muted)]">Track resources needed to execute each project — people, tools, budget allocations.</p>
      {projects.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Create a project in the Spending tab first.</p>
      ) : (
        projects.map((p) => {
          const isExpanded = expandedProject === p.project_id;
          const totalResourceCost = (p.resources || []).reduce((acc, r) => acc + (r.estimated_cost || 0), 0);
          return (
            <div key={p.project_id} className="border border-[var(--border-default)]">
              <button
                onClick={() => setExpandedProject(isExpanded ? null : p.project_id)}
                className="w-full flex items-center justify-between p-3 hover:bg-[var(--surface-alt)] transition-colors"
              >
                <div className="text-left">
                  <div className="text-sm font-semibold">{p.project_name}</div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {(p.resources || []).length} resources &middot; ${totalResourceCost.toLocaleString()} estimated
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {isExpanded && (
                <div className="border-t border-[var(--border-subtle)] p-3 space-y-3">
                  {(p.resources || []).map((r, ri) => (
                    <div key={ri} className="flex items-center gap-3 text-sm">
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-[var(--surface-alt)] border border-[var(--border-subtle)] text-[var(--text-muted)]">
                        {r.type}
                      </span>
                      <span className="flex-1">{r.name}</span>
                      <span className="text-xs font-mono text-[var(--text-muted)]">${r.estimated_cost}</span>
                    </div>
                  ))}
                  {/* Add resource form */}
                  <div className="pt-2 border-t border-[var(--border-subtle)] space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <input
                        placeholder="Resource name"
                        value={resName} onChange={e => setResName(e.target.value)}
                        className="col-span-2 px-2 py-1.5 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                      />
                      <select
                        value={resType} onChange={e => setResType(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                      >
                        {resourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <input
                        placeholder="Cost ($)"
                        type="number" value={resCost} onChange={e => setResCost(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                      />
                    </div>
                    <button
                      onClick={() => addResource(p)} disabled={saving}
                      className="text-xs font-bold px-3 py-1.5 bg-[var(--secondary)] text-white border border-[var(--border-default)] disabled:opacity-50"
                    >
                      {saving ? '...' : '+ Add Resource'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

/* ---- Cost-Benefit Tab ---- */
const CostBenefitTab = ({ onRefresh }) => {
  const [projectName, setProjectName] = useState('');
  const [costs, setCosts] = useState([{ name: '', amount: '' }]);
  const [benefits, setBenefits] = useState([{ name: '', value: '' }]);
  const [timeframe, setTimeframe] = useState(12);
  const [useAi, setUseAi] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const addCost = () => setCosts([...costs, { name: '', amount: '' }]);
  const addBenefit = () => setBenefits([...benefits, { name: '', value: '' }]);
  const removeCost = (i) => setCosts(costs.filter((_, idx) => idx !== i));
  const removeBenefit = (i) => setBenefits(benefits.filter((_, idx) => idx !== i));

  const updateCost = (i, field, val) => {
    const updated = [...costs];
    updated[i][field] = val;
    setCosts(updated);
  };
  const updateBenefit = (i, field, val) => {
    const updated = [...benefits];
    updated[i][field] = val;
    setBenefits(updated);
  };

  const runAnalysis = async () => {
    if (!projectName.trim()) { toast.error('Enter a project name'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/business-analytics/cost-benefit`, {
        project_name: projectName,
        costs: costs.filter(c => c.name).map(c => ({ name: c.name, amount: parseFloat(c.amount) || 0 })),
        benefits: benefits.filter(b => b.name).map(b => ({ name: b.name, value: parseFloat(b.value) || 0 })),
        timeframe_months: timeframe,
        use_ai: useAi
      }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {!result ? (
        <>
          <input
            data-testid="cba-project-name"
            placeholder="Project name"
            value={projectName} onChange={e => setProjectName(e.target.value)}
            className="w-full px-3 py-2 text-sm border-2 border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)] font-semibold"
          />

          {/* Costs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)]">Costs</h4>
              <button onClick={addCost} className="text-xs text-[var(--secondary)] font-bold">+ Add</button>
            </div>
            {costs.map((c, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  placeholder="Cost item"
                  value={c.name} onChange={e => updateCost(i, 'name', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                />
                <input
                  placeholder="$"
                  type="number" value={c.amount} onChange={e => updateCost(i, 'amount', e.target.value)}
                  className="w-24 px-2 py-1.5 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                />
                {costs.length > 1 && (
                  <button onClick={() => removeCost(i)} className="text-[var(--text-muted)] hover:text-[var(--primary)]"><X size={14} /></button>
                )}
              </div>
            ))}
          </div>

          {/* Benefits */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)]">Expected Benefits</h4>
              <button onClick={addBenefit} className="text-xs text-[var(--secondary)] font-bold">+ Add</button>
            </div>
            {benefits.map((b, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  placeholder="Benefit item"
                  value={b.name} onChange={e => updateBenefit(i, 'name', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                />
                <input
                  placeholder="$"
                  type="number" value={b.value} onChange={e => updateBenefit(i, 'value', e.target.value)}
                  className="w-24 px-2 py-1.5 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                />
                {benefits.length > 1 && (
                  <button onClick={() => removeBenefit(i)} className="text-[var(--text-muted)] hover:text-[var(--primary)]"><X size={14} /></button>
                )}
              </div>
            ))}
          </div>

          {/* Options */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--text-muted)]">Timeframe:</span>
              <select
                value={timeframe} onChange={e => setTimeframe(parseInt(e.target.value))}
                className="px-2 py-1 text-xs border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
              >
                <option value={3}>3 months</option>
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={useAi} onChange={e => setUseAi(e.target.checked)} className="accent-[var(--secondary)]" />
              <span className="text-xs text-[var(--text-muted)]">AI Analysis</span>
            </label>
          </div>

          <button
            data-testid="run-cba-btn"
            onClick={runAnalysis} disabled={loading}
            className="font-bold text-sm px-6 py-3 bg-[var(--primary)] text-white border-2 border-[var(--border-default)] disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <><Loader size={14} className="animate-spin" /> Analyzing...</> : 'Run Analysis'}
          </button>
        </>
      ) : (
        <CBAResult result={result} onReset={() => setResult(null)} />
      )}
    </div>
  );
};

/* ---- CBA Result ---- */
const CBAResult = ({ result, onReset }) => {
  const isPositive = result.net_value >= 0;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {result.project_name}
        </h4>
        <button onClick={onReset} className="text-xs text-[var(--text-muted)] underline">New Analysis</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Costs" value={`$${result.total_costs.toLocaleString()}`} accent="var(--primary)" />
        <MetricCard label="Total Benefits" value={`$${result.total_benefits.toLocaleString()}`} accent="var(--secondary)" />
        <MetricCard label="ROI" value={`${result.roi_percent}%`} accent={isPositive ? 'var(--secondary)' : 'var(--primary)'} />
        <MetricCard label="Payback" value={result.payback_months ? `${result.payback_months}mo` : 'N/A'} />
      </div>

      <div className={`p-3 border-2 ${isPositive ? 'border-[var(--secondary)] bg-[var(--secondary)]/5' : 'border-[var(--primary)] bg-[var(--primary)]/5'}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold uppercase tracking-widest">{result.recommendation}</span>
        </div>
        <p className="text-sm">
          Net value: <strong>${result.net_value.toLocaleString()}</strong> over {result.timeframe_months} months
        </p>
      </div>

      {result.ai_analysis && (
        <div className="p-3 border border-[var(--border-default)] bg-[var(--surface-alt)]">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={14} className="text-[var(--secondary)]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)]">AI Insight</span>
          </div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{result.ai_analysis}</p>
        </div>
      )}
    </div>
  );
};

/* ---- Shared Components ---- */
const MetricCard = ({ label, value, accent }) => (
  <div className="p-3 border border-[var(--border-subtle)] bg-[var(--surface-alt)]">
    <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</p>
    <p className="text-lg font-black" style={{ color: accent || 'var(--text-primary)', fontFamily: "'Space Grotesk', sans-serif" }}>
      {value}
    </p>
  </div>
);

export default BusinessAnalytics;
